import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import { AlertCircle, FileText, Loader2, Send, ShieldCheck, X } from 'lucide-react';
import { Button } from './ui/button';
import { Toast } from './ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import ToastPortal from './ToastPortal';
import { api } from '../services/api';

const formatMontant = (montant) => (
  `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant || 0)))} FCFA`
);

const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';

const MOTIF_MINIMUM = 10;

/**
 * Modification des frais de début d'année, par le Centre de Support.
 *
 * Ces frais vivent dans `frais_fixes` : ils sont figés à l'admission et ne
 * découlent pas des réductions, contrairement au mensuel. Ils disposaient d'une
 * route d'ajustement directe, sans validation, alors qu'ils pèsent autant que le
 * reste. Ce modal est le chemin qui passe par le Centre de Support.
 */
const ModalModifierFraisDebutAnnee = ({ eleve, anneeScolaireId, onClose, onSuccess }) => {
  const [frais, setFrais] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [toastInfo, setToastInfo] = useState(null);

  const [montant, setMontant] = useState(0);
  const [motif, setMotif] = useState('');

  const showToast = (message, type = 'success', options = {}) => {
    if (!message) return;
    setToastInfo({ message, type, ...options });
  };
  const hideToast = () => setToastInfo(null);

  useEffect(() => {
    let annule = false;

    const charger = async () => {
      setChargement(true);
      setErreur('');
      try {
        const result = await api.eleves.getFraisDebutAnnee(eleve.id, anneeScolaireId);
        if (annule) return;

        if (!result.success) {
          setErreur(result.message || "Impossible de lire les frais de début d'année.");
          return;
        }
        if (!result.data) {
          setErreur("Aucun frais de début d'année n'est enregistré pour cet élève.");
          return;
        }

        setFrais(result.data);
        setMontant(Number(result.data.montant || 0));
      } catch (error) {
        if (!annule) {
          setErreur(error?.message
            ? `Erreur lors du chargement des frais : ${error.message}`
            : "Erreur lors du chargement des frais de début d'année.");
        }
      } finally {
        if (!annule) setChargement(false);
      }
    };

    charger();
    return () => { annule = true; };
  }, [eleve.id, anneeScolaireId]);

  const nouveauMontant = Number(montant || 0);
  const ancienMontant = Number(frais?.montant || 0);
  const tarifClasse = Number(frais?.tarif_classe || 0);
  const libelle = frais?.type === 'reinscription' ? 'frais de réinscription' : "frais d'inscription";

  const ecartTarif = useMemo(() => {
    if (!frais || tarifClasse <= 0) return null;
    if (nouveauMontant === tarifClasse) return null;
    return nouveauMontant - tarifClasse;
  }, [frais, nouveauMontant, tarifClasse]);

  const riensNeChange = frais !== null && Math.round(nouveauMontant) === Math.round(ancienMontant);

  const envoyer = async () => {
    if (motif.trim().length < MOTIF_MINIMUM) {
      showToast(`Le motif doit comporter au moins ${MOTIF_MINIMUM} caractères.`, 'error');
      return;
    }

    hideToast();
    setEnvoi(true);
    try {
      const result = await api.eleves.demanderModificationFraisDebutAnnee(eleve.id, {
        annee_scolaire_id: anneeScolaireId,
        frais_fixe_id: frais.id,
        nouveau_montant: nouveauMontant,
        motif: motif.trim()
      });

      if (result.success) {
        onSuccess?.(result);
        return;
      }

      showToast(result.message || "La demande n'a pas pu être envoyée.", 'error');
      setEnvoi(false);
    } catch (error) {
      showToast(error.message || "Erreur lors de l'envoi de la demande.", 'error');
      setEnvoi(false);
    }
  };

  return (
    <>
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#246990] text-white flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div className="text-left">
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  Modifier les frais de début d'année
                </DialogTitle>
                <p className="text-sm text-gray-500">
                  {eleve.prenom} {eleve.nom}{eleve.matricule ? ` — ${eleve.matricule}` : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={envoi}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {chargement ? (
            <div className="py-16 flex items-center justify-center text-[#246990]">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Lecture des frais…
            </div>
          ) : !frais ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{erreur || 'Frais indisponibles.'}</span>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Situation actuelle</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs text-gray-500">Nature</div>
                    <div className="font-semibold text-gray-900 capitalize">{libelle}</div>
                  </div>
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs text-gray-500">Tarif de la classe</div>
                    <div className="font-semibold text-gray-900">{formatMontant(tarifClasse)}</div>
                  </div>
                  <div className="rounded-md border border-[#246990]/20 bg-[#eef7fb] p-3">
                    <div className="text-xs text-[#246990]">Montant enregistré</div>
                    <div className="font-semibold text-[#246990]">{formatMontant(ancienMontant)}</div>
                  </div>
                </div>

                {/* Un frais à zéro est marqué réglé, mais rien n'a été encaissé :
                    lui appliquer le message « déjà encaissé » laissait croire à un
                    versement qui n'a jamais eu lieu. Une exonération se dit
                    autrement, et se justifie par la remarque posée à l'admission. */}
                {Number(ancienMontant) <= 0 ? (
                  <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                    <span className="font-semibold">Ce frais est offert</span> : rien n'a été encaissé et rien
                    n'est réclamé.
                    {frais.remarque && <span className="block mt-1 text-emerald-800">{frais.remarque}</span>}
                  </div>
                ) : frais.paye ? (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Ce frais a <span className="font-semibold">déjà été encaissé</span>. La modification corrige
                    le montant enregistré : elle ne rembourse rien et ne réclame rien. Le rapprochement reste à
                    faire en caisse.
                    {frais.remarque && <span className="block mt-1 text-amber-800">{frais.remarque}</span>}
                  </div>
                ) : frais.remarque ? (
                  <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                    {frais.remarque}
                  </div>
                ) : null}
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                <label className="block text-sm">
                  <span className="text-gray-700 font-medium">Nouveau montant</span>
                  <div className="relative mt-1">
                    <input
                      type="number"
                      min="0"
                      value={montant}
                      onChange={(event) => setMontant(event.target.value)}
                      disabled={envoi}
                      className="w-full h-9 rounded-lg border border-gray-200 px-3 pr-14 focus:border-[#246990] outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
                  </div>
                </label>

                {ecartTarif !== null && (
                  <p className="text-xs text-gray-600">
                    {ecartTarif > 0
                      ? `Soit ${formatMontant(ecartTarif)} de plus que le tarif de la classe.`
                      : `Soit ${formatMontant(Math.abs(ecartTarif))} de moins que le tarif de la classe.`}
                  </p>
                )}

                <label className="block text-sm">
                  <span className="text-gray-700 font-medium">
                    Motif <span className="text-red-500">*</span>
                  </span>
                  <textarea
                    value={motif}
                    onChange={(event) => setMotif(event.target.value)}
                    placeholder="Ex : arrangement conclu avec le parent, erreur de saisie à l'admission…"
                    rows={3}
                    disabled={envoi}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15 outline-none resize-none"
                  />
                  <span className="mt-1 block text-[11px] text-gray-500">
                    Au moins {MOTIF_MINIMUM} caractères. Ce motif sera lu par le validateur et conservé dans
                    l'historique de l'élève.
                  </span>
                </label>

                {riensNeChange && (
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                    Ce montant est déjà celui enregistré : il n'y a rien à demander.
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-[#246990] mt-0.5 flex-shrink-0" />
                <div className="text-sm text-[#174866]">
                  <div className="font-semibold">Validation obligatoire</div>
                  <p className="mt-1">
                    Rien n'est appliqué maintenant : la demande part au Centre de Support et sera exécutée
                    après validation.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="px-5 py-4 border-t border-gray-200 bg-gray-50 sm:justify-between">
          <Button variant="outline" onClick={onClose} disabled={envoi} className={BOUTON_PIED_CLASSES}>
            <X className="w-4 h-4" />
            Annuler
          </Button>
          <Button
            onClick={envoyer}
            disabled={envoi || chargement || !frais || riensNeChange}
            className={`${BOUTON_PIED_CLASSES} bg-[#246990] hover:bg-[#1f5a7a] text-white`}
          >
            {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {envoi ? 'Envoi en cours…' : 'Envoyer la demande'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {toastInfo && (
      <ToastPortal>
        {shouldUseLongToast(toastInfo.message, toastInfo.type, toastInfo) ? (
          <LongToast show title={toastInfo.title} message={toastInfo.message} type={toastInfo.type} onClose={hideToast} />
        ) : (
          <Toast message={toastInfo.message} type={toastInfo.type} onClose={hideToast} />
        )}
      </ToastPortal>
    )}
    </>
  );
};

export default ModalModifierFraisDebutAnnee;
